import React, { useContext, useEffect, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';

import Text from './Text';
import DateRange from './DateRange';
import TransferHistoryTable from './TransferHistoryTable';
import { countTransferSummaries, fetchTransferSummaries } from '../api/accounts';
import { debouncer } from '../utils/core';
import { AppContext } from '../AppContext';

export const reloadSlow = process.env.REACT_APP_RELOAD_SLOW_MS || '60000';
export const reloadFast = process.env.REACT_APP_RELOAD_FAST_MS || '20000';

const slow = parseInt(reloadSlow, 10);
const fast = parseInt(reloadFast, 10);
let pollers = [];

const TransferHistory = ({ address, ...props } = {}) => {
  const {
    store,
    startDate,
    endDate,
    actions: {
      setHasActiveTransfers,
      setStartDate,
      setEndDate,
    },
  } = useContext(AppContext);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(true);
  const [transfers, setTransfers] = useState([]);
  const { hasActiveTransfers } = store;
  const [limit, setLimit] = useState(5);
  const [offset, setOffset] = useState(0);
  const [refreshed, setRefreshed] = useState(0);

  let reloadRate = slow;
  const debounce = debouncer(reloadRate - 1000);

  const addLinks = (transfer) => ({
    ...transfer,
    $link: { pathname: `/transfer/${transfer.transferId}?address=${address}` },
  });

  const updateReloadRate = (rate) => {
    if (reloadRate !== rate) {
      reloadRate = rate;
      debouncer(reloadRate - 1000);
    }
  };
  const pagination = {
    getCount: async () => {
      try {
        const result = await countTransferSummaries({ address, startDate, endDate });
        return result;
      } catch (e) {
        console.error('Failed to count transfers', e);
        setError(true);
        updateReloadRate(fast);
        return 0;
      }
    },
    getData: async ({ limit: lim, offset: off } = {}) => {
      try {
        setLoading(true);
        setLimit(lim);
        setOffset(off);
        let transfers = await fetchTransferSummaries({
          address,
          startDate,
          endDate,
          limit: lim,
          offset: off,
        });
        transfers = transfers.map(addLinks);
        setTransfers(transfers);
        setError(undefined);
        updateReloadRate(slow);
        // signal to other components that active transfers have changed?
        const activeTransfers = transfers.filter(({ completed }) => !completed);
        setHasActiveTransfers(!!activeTransfers.length);
        return transfers;
      } catch (e) {
        console.error('Failed to load transfers', e);
        setError(true);
        updateReloadRate(fast);
        return [];
      } finally {
        setLoading(false);
      }
    },
  };

  const handleDateChange = async ({ start, end }) => {
    setLoading(true);
    setStartDate(start);
    setEndDate(end);
    setRefreshed(Date.now());
    // setEffect will reset loading
  };

  const reloadTransfers = () => {
    try {
      // supress loading while reloading/polling
      setShowLoading(false);
      setRefreshed(Date.now());
    } finally {
      // allow ticks to receive change
      setTimeout(() => setShowLoading(true), 1000);
    }
  };

  useEffect(async () => {
    try {
      if (!address) {
        return undefined;
      }
      // poll for updates
      const poller = setInterval(() => debounce(reloadTransfers), reloadRate);
      // flush any existing pollers before starting
      const active = pollers || [];
      pollers = [];
      active.forEach(p => clearInterval(p));
      pollers.push(poller);
      // don't leave poller running after dispose
      return () => clearInterval(poller);
    } finally {
      setLoading(false);
    }
  }, [address, startDate, endDate, limit, offset, error]);

  // reload if active transfers change in another component
  useEffect(() => { reloadTransfers(); }, [hasActiveTransfers]);

  return (
    <Box pb="30px" {...props}>
      <Flex direction="row" alignItems="center">
        <Text textStyle="h2" as="h2" mb="40px" flexGrow="1">Transfer History</Text>
        <DateRange start={startDate} end={endDate} onChange={handleDateChange} />
      </Flex>
      {error && (
        <Flex justifyContent="center" alignItems="center">
          <Text textStyle="errorMessage">Error: failed to load transfer history</Text>
        </Flex>
      )}
      {address ? (
        <TransferHistoryTable
          loading={loading}
          showLoading={showLoading}
          transfers={transfers}
          pagination={pagination}
          refreshed={refreshed}
        />
      ) : (
        <TransferHistoryTable emptyState={() => 'Please select a wallet address'} />
      )}
    </Box>
  );
};

export default TransferHistory;
